import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getLinkIframeCard,
  getOutstandingBilling,
  processOutstandingBilling,
} from 'services/payment';
import { CustomNotification } from 'common/components';
import { useCheckCreditCardPermission } from 'hooks/useCheckCreditCardPermission';

const keys = {
  addCreditCardIframeUrl: ['payment', 'add-card'],
  getOutstandingBilling: (memberId) => [
    'payment',
    'get-outstanding-billing',
    parseInt(memberId),
  ],
  getBillingProperties: (memberId) => [
    'payment',
    'get-member-billing-properties',
    { params: { memberId: parseInt(memberId) } },
  ],
};

/**
 *? GET
 *? /api/payment/add-card
 */
const useQueryAddCreditCardIframeUrl = ({ memberId, ...options }) => {
  return useQuery({
    queryKey: keys.addCreditCardIframeUrl,
    queryFn: async () => {
      const res = await getLinkIframeCard({
        memberId,
        paymentEndpointType: null,
      });

      const { isSuccess, message, data } = res || {};

      if (!isSuccess) {
        CustomNotification.error(message || 'Server Error');
      }

      return data;
    },
    ...options,
  });
};

/**
 *? GET
 *? /api/payment/get-member-billing-properties
 */
const useQueryBillingProperties = ({ memberId, ...options }) => {
  const queryClient = useQueryClient();

  const { canAccessBilling } = useCheckCreditCardPermission();

  const query = useQuery({
    queryKey: keys.getBillingProperties(memberId),
    enabled: Boolean(memberId) && canAccessBilling,
    retry: 0,
    meta: {
      onError: () => false,
    },
    ...options,
  });

  const invalidateBillingProperties = () => {
    queryClient.invalidateQueries({
      queryKey: keys.getBillingProperties(memberId),
    });
  };

  return {
    billingProperties: query?.data ?? {},
    invalidateBillingProperties,
    ...query,
  };
};

export const getPaymentTermCache = (queryClient, memberId) => {
  return queryClient.getQueryData(keys.getBillingProperties(memberId));
};

export { keys, useQueryAddCreditCardIframeUrl, useQueryBillingProperties };

export const useQueryOutstandingBilling = ({ memberId, ...options }) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: keys.getOutstandingBilling(memberId),
    queryFn: async () => {
      const response = await getOutstandingBilling({ memberId });
      return response?.data;
    },
    ...options,
  });

  const invalidateOutstandingBilling = () => {
    queryClient.invalidateQueries({
      queryKey: keys.getOutstandingBilling(memberId),
    });
  };

  const fetchOutstandingBillingAsync = async () => {
    const { data } = await query.refetch();
    return data;
  };

  return {
    fetchOutstandingBillingAsync,
    outstandingBilling: query?.data ?? {},
    invalidateOutstandingBilling,
    ...query,
  };
};

export const useMutateProcessOutstandingBilling = () => {
  return useMutation({
    mutationFn: processOutstandingBilling,
  });
};
